export interface IdbStore<T> {
  get(key: string): Promise<T | undefined>;
  set(key: string, value: T): Promise<void>;
  delete(key: string): Promise<void>;
  /** Removes every record for which `shouldDelete` returns true. */
  prune(shouldDelete: (value: T) => boolean): Promise<void>;
}

/**
 * Creates a typed key-value store backed by a single IndexedDB object store.
 * Unlike sessionStorage/localStorage it persists structured-cloneable values
 * (Blob/File included), so it suits drafts that carry attachments.
 */
export function createIdbStore<T>(dbName: string, storeName: string): IdbStore<T> {
  let dbPromise: Promise<IDBDatabase> | null = null;

  function openDb(): Promise<IDBDatabase> {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(dbName, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    }).catch((error) => {
      // Don't cache a rejected promise: a transient open failure would
      // otherwise make the store unusable for the rest of the session.
      dbPromise = null;
      throw error;
    });
    return dbPromise;
  }

  async function withStore<R>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<R>): Promise<R> {
    const db = await openDb();
    return new Promise<R>((resolve, reject) => {
      const tx = db.transaction(storeName, mode);
      const request = run(tx.objectStore(storeName));
      tx.oncomplete = () => resolve(request.result);
      tx.onerror = () => reject(tx.error);
    });
  }

  return {
    get: (key) => withStore('readonly', (store) => store.get(key)),
    set: (key, value) => withStore('readwrite', (store) => store.put(value, key)).then(() => undefined),
    delete: (key) => withStore('readwrite', (store) => store.delete(key)).then(() => undefined),
    prune: async (shouldDelete) => {
      const db = await openDb();
      return new Promise<void>((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        const request = tx.objectStore(storeName).openCursor();
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) return;
          if (shouldDelete(cursor.value)) cursor.delete();
          cursor.continue();
        };
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    },
  };
}
